import React, { useState } from 'react';

const safeNumber = (val, decimals = 2, fallback = '—') => {
  if (val === null || val === undefined || isNaN(val) || !isFinite(val)) {
    return fallback;
  }
  return Number(val).toFixed(decimals);
};

const FutureTile = ({ name, futuresData, loading }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [showOHLCTooltip, setShowOHLCTooltip] = useState(false);

  // ✅ FIX 1: Don't show "loading" state if we have data (prevents flickering)
  if (!futuresData) {
    return null; // Just hide the tile if no data yet
  }

  const ltp = futuresData.ltp || 0;
  const priceChg = futuresData.price_change_pct || 0;
  const oiChg = futuresData.oi_change_pct || 0;
  const buildUp = futuresData.build_up || 'NEUTRAL';
  const action = futuresData.action || 'NO_ACTION';
  const commitment = futuresData.commitment || 'LOW';
  const oiSpeed = futuresData.oi_speed || 'NONE';
  const volRatio = futuresData.vol_ratio || 0;
  const volChg = futuresData.volume_change_pct;
  const expiry = futuresData.expiry || '';
  
  // ✅ FIX 2: Extract OHLC data
  const ohlc = futuresData.ohlc || {};
  const hasOHLC = ohlc.open && ohlc.high && ohlc.low && ohlc.close;

  // Color coding for action
  const getActionColor = (act) => {
    if (act === 'BUY' || act === 'HOLD_LONG') return '#00c853';
    if (act === 'SELL' || act === 'HOLD_SHORT') return '#ff1744';
    if (act === 'EXIT_LONG' || act === 'EXIT_SHORT') return '#ff9800';
    return '#78909c';
  };

  // Color for build-up
  const getBuildUpColor = (bu) => {
    if (bu === 'LONG_BUILDUP') return '#4CAF50';
    if (bu === 'SHORT_BUILDUP') return '#f44336';
    if (bu === 'SHORT_COVER') return '#ff9800';
    if (bu === 'LONG_UNWIND') return '#2196f3';
    return '#999';
  };

  // Color for commitment
  const getCommitmentColor = (com) => {
    if (com === 'HIGH') return '#00c853';
    if (com === 'MEDIUM') return '#ff9800';
    return '#999';
  };

  const actionColor = getActionColor(action);
  const buildUpColor = getBuildUpColor(buildUp);
  const commitmentColor = getCommitmentColor(commitment);
  const priceColor = priceChg > 0 ? '#4CAF50' : (priceChg < 0 ? '#f44336' : '#666');
  const oiColor = oiChg > 0 ? '#4CAF50' : (oiChg < 0 ? '#f44336' : '#666');

  return (
    <div 
      style={{
        minWidth: '135px',
        padding: '12px',
        background: 'linear-gradient(180deg, #fff3e0, #ffe0b2)',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(255,152,0,0.25)',
        border: '2px solid #ff9800',
        position: 'relative'
      }}
    >
      {/* Header */}
      <div style={{
        fontSize: '0.7rem',
        fontWeight: '700',
        color: '#e65100',
        textTransform: 'uppercase',
        marginBottom: '6px',
        textAlign: 'center'
      }}>
        📊 Futures
      </div>

      {/* ✅ FIX 3: LTP with OHLC tooltip on hover */}
      <div 
        style={{
          fontSize: '1.2rem',
          fontWeight: '900',
          color: '#333',
          textAlign: 'center',
          marginBottom: '4px',
          cursor: hasOHLC ? 'pointer' : 'default',
          position: 'relative'
        }}
        onMouseEnter={() => hasOHLC && setShowOHLCTooltip(true)}
        onMouseLeave={() => setShowOHLCTooltip(false)}
      >
        ₹{safeNumber(ltp)}
        
        {/* OHLC Tooltip */}
        {showOHLCTooltip && hasOHLC && (
          <div style={{
            position: 'absolute',
            background: 'rgba(0,0,0,0.9)',
            color: '#fff',
            padding: '8px 12px',
            borderRadius: '6px',
            fontSize: '0.65rem',
            fontWeight: '600',
            whiteSpace: 'nowrap',
            zIndex: 1000,
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginTop: '6px',
            pointerEvents: 'none'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', gap: '4px 12px', textAlign: 'left' }}>
              <span>Open:</span><span style={{ color: '#2196f3', fontWeight: 800 }}>₹{safeNumber(ohlc.open)}</span>
              <span>High:</span><span style={{ color: '#4CAF50', fontWeight: 700 }}>₹{safeNumber(ohlc.high)}</span>
              <span>Low:</span><span style={{ color: '#f44336', fontWeight: 700 }}>₹{safeNumber(ohlc.low)}</span>
              <span>Prev Close:</span><span style={{ color: '#ff9800', fontWeight: 700 }}>₹{safeNumber(ohlc.close)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Price & OI Changes */}
      <div style={{
        display: 'flex',
        gap: '8px',
        justifyContent: 'center',
        marginBottom: '8px'
      }}>
        <div style={{
          fontSize: '0.65rem',
          fontWeight: '700',
          color: priceColor
        }}>
          Price: {priceChg > 0 ? '+' : ''}{safeNumber(priceChg, 1)}%
        </div>
        <div style={{
          fontSize: '0.65rem',
          fontWeight: '700',
          color: oiColor
        }}>
          OI: {oiChg > 0 ? '+' : ''}{safeNumber(oiChg, 1)}%
        </div>

      {/* volume */}
      {typeof volChg === 'number' && (
        <div
          style={{
            fontSize: '0.65rem',
            fontWeight: '700',
            color:
              volChg > 0 ? '#4CAF50' :
              volChg < 0 ? '#f44336' :
              '#666'
          }}
        >
          Vol: {volChg > 0 ? '+' : ''}
          {safeNumber(volChg, 1)}%
        </div>
      )}

      </div>



      {/* Build-up */}
      <div style={{
        padding: '4px 8px',
        background: 'rgba(255,255,255,0.5)',
        borderRadius: '6px',
        marginBottom: '6px'
      }}>
        <div style={{
          fontSize: '0.6rem',
          fontWeight: '700',
          color: buildUpColor,
          textAlign: 'center'
        }}>
          {buildUp.replace(/_/g, ' ')}
        </div>
      </div>

      {/* Action - ✅ FIX 4: Add hover for details */}
      <div 
        style={{
          padding: '6px 10px',
          background: `${actionColor}20`,
          border: `1.5px solid ${actionColor}`,
          borderRadius: '8px',
          marginBottom: '6px',
          cursor: 'pointer',
          position: 'relative'
        }}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <div style={{
          fontSize: '0.75rem',
          fontWeight: '900',
          color: actionColor,
          textAlign: 'center'
        }}>
          {action.replace(/_/g, ' ')}
        </div>

        {/* Details Tooltip */}
        {showTooltip && (
          <div style={{
            position: 'absolute',
            background: 'rgba(0,0,0,0.9)',
            color: '#fff',
            padding: '10px 14px',
            borderRadius: '8px',
            fontSize: '0.65rem',
            fontWeight: '600',
            whiteSpace: 'nowrap',
            zIndex: 1000,
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginTop: '8px',
            minWidth: '220px',
            pointerEvents: 'none'
          }}>
            <div style={{ marginBottom: '6px', fontWeight: '800', color: '#ff9800' }}>
              Futures Analysis
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', gap: '4px 12px', textAlign: 'left' }}>
              <span>Price Δ:</span><span style={{ color: priceColor, fontWeight: 800 }}>{priceChg > 0 ? '+' : ''}{safeNumber(priceChg, 1)}%</span>
              <span>OI Δ:</span><span style={{ color: oiColor, fontWeight: 800 }}>{oiChg > 0 ? '+' : ''}{safeNumber(oiChg, 1)}%</span>
              <span>Build-up:</span><span style={{ color: buildUpColor, fontWeight: 700 }}>{buildUp.replace(/_/g, ' ')}</span>
              <span>Commitment:</span><span style={{ color: commitmentColor, fontWeight: 700 }}>{commitment}</span>
              <span>OI Speed:</span><span style={{ color: '#2196f3', fontWeight: 700 }}>{oiSpeed}</span>
              <span>Vol Ratio:</span><span style={{ color: '#9c27b0', fontWeight: 700 }}>{safeNumber(volRatio, 2)}</span>
              <span>Vol Δ:</span><span style={{ color: '#9c27b0', fontWeight: 700 }}>{volChg > 0 ? '+' : ''}{safeNumber(volChg, 1)}%</span>


              {expiry && (
                <>
                  <span>Expiry:</span><span style={{ color: '#ffc107', fontWeight: 700 }}>{expiry}</span>
                </>
              )}
            </div>
            <div style={{ marginTop: '6px', fontSize: '0.55rem', color: '#aaa', fontStyle: 'italic', textAlign: 'left' }}>
              {futuresData.interpretation || 'No interpretation available'}
            </div>
          </div>
        )}
      </div>

      {/* Commitment & Speed */}
      <div style={{
        display: 'flex',
        gap: '6px',
        justifyContent: 'space-between',
        fontSize: '0.6rem',
        fontWeight: '700'
      }}>
        <div style={{ color: commitmentColor }}>
          Commit: {commitment}
        </div>
        <div style={{ color: '#666' }}>
          Speed: {oiSpeed}
        </div>
      </div>
    </div>
  );
};

export default FutureTile;